import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getShareAccessToken } from "../../services/shareService";
import { GetUserResponse, Share, ShareState } from "../../proto/api_pb";
import { ShareType, ShareTypeMap } from "../../proto/api_pb";
import { GetShareAccessTokenResponse } from "../../proto/api_pb";
import { SingleTrack } from "../TrackPage/TrackPage";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import PageLayout from "../../components/PageLayout/PageLayout";
import { getPublicUser } from "../../services/userService";
import { styles } from "./styles";
import { useAuth0 } from "@auth0/auth0-react";
import { SharedContentPageProps } from "./types";

export const SharedContentPage: React.FC<SharedContentPageProps> = ({
  currentUser,
  session,
  setInbox,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [share, setShare] = useState<Share>();
  const [shareToken, setShareToken] = useState<string>();
  const [shareType, setShareType] =
    useState<ShareTypeMap[keyof ShareTypeMap]>();
  const [shareObjectId, setShareObjectId] = useState<string>();
  const [unlimitedViews, setUnlimitedViews] = useState<boolean>();
  const [allowDownload, setIsAllowDownload] = useState<boolean>();
  const [sharePageTitle, setSharePageTitle] = useState<string>();
  const [ownerDetails, setOwnerDetails] = useState<GetUserResponse>();
  const [isMaxViewsReached, setIsMaxViewsReached] = useState<boolean>(false);
  const { id } = useParams();
  const { loginWithRedirect } = useAuth0();

  const renderError = (text: string) => {
    return (
      <Box sx={{ height: "55vh" }}>
        <PageLayout>
          <Typography sx={{ textAlign: "center" }} variant="h4">
            {text}
          </Typography>
        </PageLayout>
      </Box>
    );
  };

  useEffect(() => {
    setLoading(true);
    const helper = async () => {
      if (id) {
        const res = await getShareAccessToken(id);
        if (res instanceof GetShareAccessTokenResponse) {
          const ownerId: string = res.getShare()?.getOwnerId() ?? "";
          setIsAllowDownload(res.getShare()?.getAllowDownload());
          setShare(res.getShare());
          setShareToken(res.getAccessToken());
          setShareType(res.getShare()?.getShareType());
          setShareObjectId(res.getShare()?.getObjectId());
          setUnlimitedViews(res.getShare()?.getUnlimitedViews());
          setLoading(false);

          const getUserResponse = await getPublicUser(
            res.getAccessToken(),
            ownerId
          );
          if (getUserResponse instanceof GetUserResponse) {
            setOwnerDetails(getUserResponse);
          }
        } else {
          if (res === undefined) {
            setIsMaxViewsReached(true);
          }
        }
      }
    };
    helper();
  }, [id]);

  if (share) {
    if (
      share.getShareState() !== ShareState.SHARE_STATE_DELETED &&
      (unlimitedViews || share.getExpiryTimestamp() >= Date.now()) &&
      share.getMaxViews() >= share.getImpressionCount()
    ) {
      if (shareType === ShareType.SHARE_TYPE_TRACK) {
        return (
          <Box sx={styles.mainContainer}>
            <Box sx={{ width: "100%", margin: "20px" }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography sx={{ fontWeight: "bold" }} variant="h5">
                  {`${
                    ownerDetails?.getUsername() ?? "Ommuse user"
                  } has shared ${sharePageTitle} with you.`}
                </Typography>
                {!currentUser && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: "10px",
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      onClick={() => {
                        loginWithRedirect({
                          appState: { returnTo: "/my-tracks" },
                          authorizationParams: { screen_hint: "login" },
                        });
                      }}
                      sx={styles.button}
                    >
                      Log in
                    </Button>
                    <Button
                      onClick={() => {
                        loginWithRedirect({
                          appState: { returnTo: "/my-tracks" },
                          authorizationParams: { screen_hint: "signup" },
                        });
                      }}
                      sx={styles.button}
                    >
                      Sign up
                    </Button>
                  </Box>
                )}
              </Box>
              <SingleTrack
                userVerified={false}
                singleTrackPage={true}
                shareObjectId={shareObjectId}
                shareToken={shareToken}
                allowDownload={allowDownload}
                setSharePageTitle={setSharePageTitle}
                session={session}
                setInbox={setInbox}
                shareId={id ?? ""}
              />
            </Box>
          </Box>
        );
      }
    } else {
      if (!unlimitedViews && share.getMaxViews() < share.getImpressionCount()) {
        return renderError("Views limit is over!");
      } else if (share.getShareState() === ShareState.SHARE_STATE_DELETED) {
        return renderError("Not found!");
      } else if (unlimitedViews || share.getExpiryTimestamp() < Date.now()) {
        return renderError("Link expired!");
      }
    }
  }
  return (
    <>
      <Box sx={styles.loadingContainer}>
        {loading && !isMaxViewsReached && <CircularProgress />}
        {isMaxViewsReached && renderError("Views limit is over!")}
      </Box>
      {!loading && renderError("Link expired!")}
    </>
  );
};
